// Helpers del Resumen ejecutivo: score de riesgo, RAG del proyecto y catálogos.
// Los valores de catálogo son códigos canónicos en inglés; la etiqueta visible
// se traduce en el componente vía i18n.

export const RISK_CRITICAL = 10 // I×P ≥ 10 => riesgo contractual crítico (rojo)

// Catálogos (código → se traduce en el componente).
export const RISK_TYPES = ['threat', 'opportunity']
export const RISK_STATUSES = ['open', 'in_progress', 'closed']
export const RISK_ACTIONS = ['avoid', 'mitigate', 'transfer', 'accept', 'exploit', 'improve', 'share']
export const LEVELS = [1, 2, 3, 4, 5]

// Score de un riesgo = impacto × probabilidad.
export function riskScore (r) {
  return (Number(r.impact) || 0) * (Number(r.probability) || 0)
}

// Opciones del select de responsable a partir de los usuarios del proyecto (sources.users).
export function userOptions (users) {
  return (users || []).map((name) => ({ value: name, label: name }))
}

// Color del score (para resaltar la fila): rojo ≥ 10, ámbar 5–9, verde < 5.
export function scoreColor (score) {
  if (score >= RISK_CRITICAL) return 'red'
  if (score >= 5) return 'amber'
  return 'green'
}

// Importe de un hito de pago = % sobre el importe total del contrato (PRM).
export function milestoneAmount (row, totalAmount) {
  return (Number(row.pct) || 0) / 100 * (Number(totalAmount) || 0)
}

// DD/MM/AAAA -> Date (medianoche local); null si no parsea. Las fechas de S03 (facturas, hitos)
// se persisten en DD/MM/AAAA (ver edit-field.fromISO).
function parseDMY (s) {
  const m = /^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{2,4})$/.exec(String(s || '').trim())
  if (!m) return null
  let y = m[3]
  if (y.length === 2) y = '20' + y
  const d = new Date(Number(y), Number(m[2]) - 1, Number(m[1]))
  return isNaN(d.getTime()) ? null : d
}

// Totales de facturación a partir del PLAN DE COBRO actual (hitos con facturas anidadas,
// s03.paymentPlan). Importes € = importe local × tipo de cambio. Una factura está VENCIDA si no
// está cobrada y su vencimiento (emisión + días de cobro) ya pasó. `today` en YYYY-MM-DD.
export function paymentPlanTotals (paymentPlan, rate, collectionDays, today) {
  const num = (v) => Number(v) || 0
  const r = Number(rate) || 0
  const days = Number(collectionDays) || 0
  const todayDate = today ? new Date(today + 'T00:00:00') : new Date()
  let invoicedLocal = 0
  let collectedLocal = 0
  let overdueLocal = 0
  for (const m of paymentPlan || []) {
    for (const inv of m.invoices || []) {
      const amount = num(inv.importeLocal)
      const collected = num(inv.importeCobrado)
      invoicedLocal += amount
      collectedLocal += collected
      const emision = parseDMY(inv.emision)
      if (emision && inv.cobrado !== 'yes') {
        const due = new Date(emision)
        due.setDate(due.getDate() + days)
        if (due < todayDate) overdueLocal += Math.max(0, amount - collected)
      }
    }
  }
  return {
    invoiced: invoicedLocal * r,
    collected: collectedLocal * r,
    notCollected: (invoicedLocal - collectedLocal) * r,
    overdue: overdueLocal * r,
  }
}

// Próximo hito de cobro: el hito con fecha estimada futura más cercana (s03.paymentPlan).
export function nextPaymentMilestone (paymentPlan, today) {
  const todayDate = today ? new Date(today + 'T00:00:00') : new Date()
  const future = (paymentPlan || [])
    .map((m) => ({ m, d: parseDMY(m.fechaEstimada) }))
    .filter((x) => x.d && x.d >= todayDate)
    .sort((a, b) => a.d - b.d)
  if (!future.length) return null
  const m = future[0].m
  return { description: m.name || m.condicionante || '', estimatedDate: m.fechaEstimada }
}

// Datos completos de la cartilla "Facturación, cobros e hitos" (Dashboard y S03). Fuente única.
export function billingCardData (s03, totalAmount, today) {
  s03 = s03 || {}
  const rate = (Number.isFinite(Number(s03.exchangeRate)) && Number(s03.exchangeRate) > 0) ? Number(s03.exchangeRate) : 1
  const t = paymentPlanTotals(s03.paymentPlan, rate, s03.collectionDays, today)
  const contractEur = (Number(s03.contractAmountLocal) || 0) * rate
  // Base de "pendiente de facturar": el importe del contrato (€) si el usuario lo rellenó; si está
  // vacío, la venta total del PRM (así no sale un negativo espurio = 0 - facturado).
  const invoiceBase = (Number(s03.contractAmountLocal) || 0) !== 0 ? contractEur : (Number(totalAmount) || 0)
  const ratioOverdue = t.invoiced > 0 ? t.overdue / t.invoiced : 0
  return {
    totalSale: Number(totalAmount) || 0,
    invoiced: t.invoiced,
    collected: t.collected,
    pendingToInvoice: invoiceBase - t.invoiced, // (importe del contrato € o venta total) - facturado
    pendingToCollect: t.notCollected,
    overdue: t.overdue,
    nextMilestone: nextPaymentMilestone(s03.paymentPlan, today),
    status: ratioOverdue > 0.05 ? 'red' : ratioOverdue > 0.01 ? 'amber' : 'green',
  }
}

// Penalidad total = min(Σ calculadas, importe total × CAP%).
export function penaltyTotal (rows, totalAmount, capPct) {
  const sum = (rows || []).reduce((s, r) => s + (Number(r.calculated) || 0), 0)
  const cap = (Number(totalAmount) || 0) * ((Number(capPct) || 0) / 100)
  return cap > 0 ? Math.min(sum, cap) : sum
}

// RAG del proyecto (S01). Cada criterio solo se aplica si hay datos.
// rojo: desv. avance < -10pp, desv. margen < -2pp, riesgo abierto I×P >= 10, o >= 2 OSH.
// ambar: desv. avance -10..-5, desv. margen -2..0, o alguna OSH. Margenes como fraccion.
export function computeRag ({ progressReal, progressScheduled, marginActual, marginCommercial, risks, oshOpen }) {
  const criticalRisk = (risks || []).some((r) => r.status !== 'closed' && riskScore(r) >= RISK_CRITICAL)
  const osh = Number(oshOpen) || 0

  const hasProgress = isNum(progressReal) && isNum(progressScheduled)
  const progressDev = hasProgress ? Number(progressReal) - Number(progressScheduled) : null

  const hasMargin = isNum(marginActual) && isNum(marginCommercial)
  const marginDev = hasMargin ? (Number(marginActual) - Number(marginCommercial)) * 100 : null

  if ((progressDev !== null && progressDev < -10) || (marginDev !== null && marginDev < -2) || criticalRisk || osh >= 2) return 'red'
  if ((progressDev !== null && progressDev < -5) || (marginDev !== null && marginDev < 0) || osh > 0) return 'amber'
  return 'green'
}

function isNum (v) {
  return v !== null && v !== undefined && v !== '' && !isNaN(Number(v))
}
